import { config } from '../config';
import { watchCommit } from '../data/commit';
import { db, reopenCold, warmConnection } from '../data/harnessDb';
import { deriveStatus } from '../domain/status';
import { MeasurementError, StorageError, errorMessage } from '../app/errors';
import { flushCount, log, pauseFlush, resetFlushCount, resumeFlush } from '../app/logger';
import type { HypothesisRow, ObservationRow, TrialSample } from '../types';

/**
 * The measured transaction, and the instrument around it.
 *
 * ── What is measured, exactly ────────────────────────────────────────────────
 * Design §5.1 step 7: "One `readwrite` transaction over `['observations','hypotheses']` — exactly
 * two rows, no blob, no log write, no `matches` row." This module performs precisely that:
 *
 *   1. insert one `Observation` row (`kind: 'increment'`)
 *   2. read the target `Hypothesis` — the in-transaction snapshot design §1.1 requires, so there
 *      is no read-then-write race under the single-flight queue
 *   3. write back `{cachedCount, status, lastIncrementTs}`, with `status` produced by the one
 *      expression in `domain/status.ts`
 *
 * The in-transaction read is included deliberately. A blind write would be marginally faster and
 * would not be the transaction the product performs; between an optimistic measurement and a
 * conservative one, only the conservative one can settle A-1.
 *
 * ── The three timestamps ─────────────────────────────────────────────────────
 *   anchor   `pointerup` in live-tap mode, or the moment before dispatch in auto mode
 *   complete the **underlying `IDBTransaction`'s `complete` event** — design §5.1 step 8 names
 *            this exact event as where acknowledgment is registered, so that is where the
 *            instrument sits. Not Dexie's promise resolution, which is a microtask later.
 *   painted  the next animation frame, i.e. the earliest moment LIVE-2's flash can reach the glass
 *
 * `commitMs` (anchor→complete, minus input) answers A-1. `totalMs` (anchor→painted) answers NFR-1.
 * Separating them matters: a slow IndexedDB and a slow frame boundary have different remedies, and
 * a single blended number would hide which one is in play.
 *
 * ── The logger is held off ───────────────────────────────────────────────────
 * `pauseFlush()` wraps every measured window and the run record carries `logFlushesDuringRun`,
 * which must be zero. Per `fire-and-forget-observability` and design §12.1, an observability write
 * inside the measured window would corrupt the measurement as well as the critical path.
 */

const MODULE = 'runner';

export interface TrialContext {
  hypothesisId: string;
  matchId: string;
  /** Monotonic clock. Injected so tests can drive it and so the pure path stays clock-free. */
  now: () => number;
  /**
   * Invoked synchronously on the `complete` event — this is where the product toggles the LIVE-2
   * ack class on the tile ref. Live-tap mode passes the real DOM work so it is inside the window.
   */
  onCommitted?: () => void;
}

/** Sequence counter, so ids never collide across trials within a run. */
let trialSeq = 0;

/**
 * Runs one measured two-row `readwrite`.
 *
 * @param anchorMs the timestamp the latency is measured from. Live-tap mode passes the
 *   `pointerup` timestamp; auto mode passes `now()` taken immediately before the call.
 * @throws {MeasurementError} if the transaction aborts, if the target hypothesis is missing, or if
 *   the `complete` event never fires. Never returns a partial sample — a trial that did not commit
 *   is recorded as a failure, not as a fast one (NFR-8).
 */
export async function runOneTrial(
  index: number,
  anchorMs: number,
  pageLoadMs: number,
  ctx: TrialContext,
): Promise<TrialSample> {
  const handle = db();
  const { now } = ctx;

  const seq = trialSeq++;
  const observation: ObservationRow = {
    id: `u0-${seq.toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
    hypothesisId: ctx.hypothesisId,
    matchId: ctx.matchId,
    matchMinute: seq % 90,
    kind: 'increment',
    audioBlobId: null,
    createdAt: Date.now(),
  };

  /**
   * The commit timestamp, resolved from the underlying `IDBTransaction`'s `complete` event.
   *
   * ── Why this is a promise rather than a variable sampled afterwards ──────────
   * Design §5.1 step 8 registers the acknowledgment on the native `complete` event, and step 9
   * renders the flash from that event's handler. **Dexie's transaction promise resolves before
   * that event arrives** — verified live against Chromium, where the listener attaches to a
   * `readwrite` transaction, the scope completes, `await db.transaction(...)` resolves, and
   * `complete` has still not fired. Reading a variable after the `await` therefore samples too
   * early and every trial fails.
   *
   * `fake-indexeddb` does not reproduce this: it dispatches `complete` inside the same
   * microtask checkpoint, so the Node suite passed while the browser could not take a single
   * measurement. That is exactly why a harness cannot be signed off from unit tests.
   *
   * Awaiting the event is also the *more faithful* instrument. It measures the same moment the
   * product's ack will fire from, rather than a Dexie bookkeeping milestone that happens earlier
   * and would have reported a flattering number.
   *
   * BUILD-UNIT NOTE (U-3): the listener-attachment logic now lives in `data/commit.ts` and is
   * shared with the product's `runWrite()`. Two copies of something this subtle is how one of them
   * ends up sampling the wrong moment — which is the exact defect this comment describes.
   */
  const watch = watchCommit({
    now,
    timeoutMs: config.HARNESS_COMMIT_EVENT_TIMEOUT_MS,
    // Inside the measured window, exactly where design §5.1 step 9 puts the ack.
    onCommitted: () => ctx.onCommitted?.(),
    makeTimeoutError: (diagnostic, ms) =>
      new MeasurementError(
        `IDBTransaction "complete" never arrived within ${ms} ms (${diagnostic}). The commit timestamp is unavailable, so this trial cannot be scored.`,
        { context: { index, diagnostic } },
      ),
    makeAbortError: () =>
      new MeasurementError('The measured transaction aborted; nothing was committed.', {
        context: { index },
      }),
  });
  const attachCompleteListener = watch.attach;

  const dispatchAt = now();

  try {
    await handle.transaction('rw', handle.observations, handle.hypotheses, async () => {
      attachCompleteListener();

      await handle.observations.add(observation);
      attachCompleteListener();

      const row = (await handle.hypotheses.get(ctx.hypothesisId)) as HypothesisRow | undefined;
      if (!row) {
        throw new MeasurementError('Target hypothesis is missing; the database is not seeded.', {
          context: { hypothesisId: ctx.hypothesisId },
        });
      }

      const cachedCount = row.cachedCount + 1;
      await handle.hypotheses.put({
        ...row,
        cachedCount,
        lastIncrementTs: observation.createdAt,
        status: deriveStatus(row.statusOverride, cachedCount),
      });
      attachCompleteListener();
    });
  } catch (cause) {
    if (cause instanceof MeasurementError) throw cause;
    throw new MeasurementError(`Measured transaction failed: ${errorMessage(cause)}`, {
      cause,
      context: { index, hypothesisId: ctx.hypothesisId },
    });
  }

  // Bounded by `watchCommit`'s own timer. A trial that never commits must become a recorded
  // failure, not a hang — a harness that stalls at a venue with no explanation is worse than one
  // that reports "this did not work".
  const completeAt = await watch.committed;

  const paintedAt = await nextFrame(now);

  return {
    index,
    inputMs: Math.max(0, dispatchAt - anchorMs),
    commitMs: completeAt - dispatchAt,
    paintMs: paintedAt - completeAt,
    totalMs: paintedAt - anchorMs,
    sinceLoadMs: dispatchAt - pageLoadMs,
  };
}

function rejectAfter(ms: number, makeError: () => Error): Promise<never> {
  return new Promise<never>((_, reject) => {
    setTimeout(() => reject(makeError()), ms);
  });
}

/**
 * Resolves at the next animation frame, returning the monotonic time it ran at — the earliest
 * moment LIVE-2's flash can reach the glass.
 *
 * Bounded, because iOS stops delivering frames to a backgrounded page. Without the bound, switching
 * apps mid-run would leave the harness waiting forever with a progress bar that never moves.
 */
function nextFrame(now: () => number): Promise<number> {
  if (typeof requestAnimationFrame !== 'function') {
    // Node / test environment: settle on a macrotask so the shape of the promise is unchanged.
    return new Promise<number>((resolve) => setTimeout(() => resolve(now()), 0));
  }
  return Promise.race([
    new Promise<number>((resolve) => requestAnimationFrame(() => resolve(now()))),
    rejectAfter(
      config.HARNESS_FRAME_TIMEOUT_MS,
      () =>
        new MeasurementError(
          `No animation frame was delivered within ${config.HARNESS_FRAME_TIMEOUT_MS} ms. The app was probably backgrounded — keep it in the foreground for the whole run.`,
        ),
    ),
  ]);
}

// ─────────────────────────────────────────────────────────────────────────────
// Run orchestration
// ─────────────────────────────────────────────────────────────────────────────

export type Pacing = 'burst' | 'realistic';

export interface AutoRunOptions {
  trials: number;
  warmups: number;
  pacing: Pacing;
  hypothesisId: string;
  matchId: string;
  now: () => number;
  pageLoadMs: number;
  onProgress?: (done: number, total: number, lastSample: TrialSample | null) => void;
  shouldStop?: () => boolean;
}

export interface AutoRunResult {
  samples: TrialSample[];
  failures: { index: number; message: string }[];
  warmupTrials: number;
  logFlushesDuringRun: number;
}

/**
 * Runs the warm-up trials, discards them, then measures.
 *
 * Warm-ups are discarded rather than reported because JIT compilation and Dexie's first-call
 * initialisation are one-time costs the product pays once per launch, not per tap. Counting them
 * into a p99 would produce a number no user ever experiences. The *cold* condition is how genuine
 * first-tap cost is captured, and it reports its first trial explicitly.
 */
export async function runAuto(options: AutoRunOptions): Promise<AutoRunResult> {
  const { trials, warmups, pacing, now, pageLoadMs } = options;

  if (trials < config.HARNESS_TRIALS_MIN || trials > config.HARNESS_TRIALS_MAX) {
    throw new MeasurementError(
      `Trial count ${trials} is outside [${config.HARNESS_TRIALS_MIN}, ${config.HARNESS_TRIALS_MAX}].`,
    );
  }

  const ctx: TrialContext = { hypothesisId: options.hypothesisId, matchId: options.matchId, now };
  const samples: TrialSample[] = [];
  const failures: { index: number; message: string }[] = [];

  resetFlushCount();
  pauseFlush();

  try {
    for (let i = 0; i < warmups; i++) {
      if (options.shouldStop?.()) break;
      try {
        await runOneTrial(-1 - i, now(), pageLoadMs, ctx);
      } catch (cause) {
        // A failing warm-up is worth knowing about but must not abort the run.
        failures.push({ index: -1 - i, message: `warm-up: ${errorMessage(cause)}` });
      }
    }

    for (let i = 0; i < trials; i++) {
      if (options.shouldStop?.()) break;
      try {
        const anchor = now();
        const sample = await runOneTrial(i, anchor, pageLoadMs, ctx);
        samples.push(sample);
        options.onProgress?.(i + 1, trials, sample);
      } catch (cause) {
        failures.push({ index: i, message: errorMessage(cause) });
        options.onProgress?.(i + 1, trials, null);
      }

      if (pacing === 'realistic') {
        await new Promise<void>((resolve) =>
          setTimeout(resolve, config.HARNESS_PACING_REALISTIC_MS),
        );
      }
    }
  } finally {
    resumeFlush();
  }

  const logFlushesDuringRun = flushCount();
  if (logFlushesDuringRun > 0) {
    log.error(
      MODULE,
      'OBS-1 flushed inside the measured window — this run’s figures include logger I/O and must not be used for A-1',
      { logFlushesDuringRun },
    );
  }

  return { samples, failures, warmupTrials: warmups, logFlushesDuringRun };
}

/**
 * Prepares the connection for a condition.
 *
 * `warm` reproduces design §5.1's protection — a no-op `readwrite` on entering an in-match state.
 * `cold` closes and reopens the Dexie connection instead, so the first measured trial pays the
 * connection's first-transaction cost. `thermal` warms, because the variable under test there is
 * the device's clock throttle, not the connection.
 *
 * @throws {StorageError} if the connection cannot be established.
 */
export async function prepareConnection(
  condition: 'warm' | 'cold' | 'thermal',
  now: () => number,
): Promise<{ openMs: number }> {
  if (condition === 'cold') {
    const { openMs } = await reopenCold(now);
    log.info(MODULE, 'cold: connection closed and reopened', { openMs });
    return { openMs };
  }

  const t0 = now();
  await warmConnection();
  const openMs = now() - t0;
  log.info(MODULE, `${condition}: connection warmed`, { warmMs: openMs });
  return { openMs };
}

/**
 * ENV-8-consistent wake lock, held for the duration of a run.
 *
 * BUILD-UNIT NOTE: this is a deliberate thin slice of U-20's `app/wakeLock.ts` — request and
 * release, nothing more. It is here because a two-minute thermal soak on a phone at a venue is
 * useless if the screen sleeps halfway through, and because a sleeping screen changes the very
 * thermal state being measured. A rejection is logged and never retried in a loop (design §13).
 */
export async function withWakeLock<T>(work: () => Promise<T>): Promise<T> {
  type WakeLockSentinelLike = { release: () => Promise<void> };
  const nav = navigator as Navigator & {
    wakeLock?: { request: (type: 'screen') => Promise<WakeLockSentinelLike> };
  };

  let sentinel: WakeLockSentinelLike | null = null;
  try {
    sentinel = (await nav.wakeLock?.request('screen')) ?? null;
    if (sentinel) log.info(MODULE, 'screen wake lock acquired');
    else log.warn(MODULE, 'Screen Wake Lock API unavailable; the display may sleep mid-run');
  } catch (cause) {
    log.exception(MODULE, 'wake lock request rejected; continuing without it', cause);
  }

  try {
    return await work();
  } finally {
    if (sentinel) {
      try {
        await sentinel.release();
        log.info(MODULE, 'screen wake lock released');
      } catch (cause) {
        log.exception(MODULE, 'wake lock release failed', cause);
      }
    }
  }
}

/** Test seam: makes ids deterministic across cases. */
export function __resetTrialSeqForTests(): void {
  trialSeq = 0;
}

export { StorageError };
